import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const DEBOUNCE_MS = 500;

function isFiniteNumber(value) {
    if (value == null || value === '') return false;
    return Number.isFinite(Number(value));
}

export function buildDcfPayload(state) {
    const forecastYears = Number(state.forecastYears);
    const stage1Years = Number(state.stage1Years);
    const stage2Years = Math.max(
        Number(state.stage2Years),
        forecastYears - stage1Years,
    );

    return {
        base_revenue: Number(state.baseRevenue),
        revenue_growth_stages: [
            { years: stage1Years, rate: Number(state.stage1Growth) },
            { years: stage2Years, rate: Number(state.stage2Growth) },
        ],
        operating_margin_start: Number(state.operatingMarginStart),
        operating_margin_target: Number(state.operatingMarginTarget),
        tax_rate: Number(state.taxRate),
        d_and_a_pct_revenue: Number(state.dAndAPctRevenue),
        capex_pct_revenue: Number(state.capexPctRevenue),
        nwc_pct_revenue_change: Number(state.nwcPctRevenueChange),
        wacc: Number(state.wacc),
        terminal_growth: Number(state.terminalGrowth),
        forecast_years: forecastYears,
        starting_fcf: Number(state.startingFcf),
        fcf_growth_rate: Number(state.fcfGrowthRate),
        net_debt: Number(state.netDebt),
        shares_outstanding: Number(state.sharesOutstanding),
        sensitivity: true,
    };
}

export function useDcfCalculation(state) {
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const {
        baseRevenue,
        stage1Years,
        stage1Growth,
        stage2Years,
        stage2Growth,
        operatingMarginStart,
        operatingMarginTarget,
        taxRate,
        dAndAPctRevenue,
        capexPctRevenue,
        nwcPctRevenueChange,
        wacc,
        terminalGrowth,
        forecastYears,
        startingFcf,
        fcfGrowthRate,
        netDebt,
        sharesOutstanding,
    } = state;
    const payload = useMemo(() => buildDcfPayload({
        baseRevenue,
        stage1Years,
        stage1Growth,
        stage2Years,
        stage2Growth,
        operatingMarginStart,
        operatingMarginTarget,
        taxRate,
        dAndAPctRevenue,
        capexPctRevenue,
        nwcPctRevenueChange,
        wacc,
        terminalGrowth,
        forecastYears,
        startingFcf,
        fcfGrowthRate,
        netDebt,
        sharesOutstanding,
    }), [
        baseRevenue,
        stage1Years,
        stage1Growth,
        stage2Years,
        stage2Growth,
        operatingMarginStart,
        operatingMarginTarget,
        taxRate,
        dAndAPctRevenue,
        capexPctRevenue,
        nwcPctRevenueChange,
        wacc,
        terminalGrowth,
        forecastYears,
        startingFcf,
        fcfGrowthRate,
        netDebt,
        sharesOutstanding,
    ]);
    const ready = state.seeded && [
        state.baseRevenue,
        state.stage1Years,
        state.stage1Growth,
        state.stage2Years,
        state.stage2Growth,
        state.operatingMarginStart,
        state.operatingMarginTarget,
        state.taxRate,
        state.dAndAPctRevenue,
        state.capexPctRevenue,
        state.nwcPctRevenueChange,
        state.wacc,
        state.terminalGrowth,
        state.forecastYears,
        state.startingFcf,
        state.fcfGrowthRate,
        state.netDebt,
        state.sharesOutstanding,
    ].every(isFiniteNumber);

    useEffect(() => {
        if (!ready) return undefined;

        let active = true;
        setLoading(true);
        setError(null);
        const timeout = setTimeout(async () => {
            try {
                const apiUrl = process.env.REACT_APP_API_URL || '';
                const response = await axios.post(`${apiUrl}/api/dcf`, payload);
                if (active) setResult(response.data);
            } catch (requestError) {
                if (active) {
                    setError(
                        requestError?.response?.data?.error
                        || requestError.message
                        || 'Unable to calculate the DCF.',
                    );
                }
            } finally {
                if (active) setLoading(false);
            }
        }, DEBOUNCE_MS);

        return () => {
            active = false;
            clearTimeout(timeout);
        };
    }, [payload, ready]);

    return { result, loading, error };
}
